import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { BalanceProvider } from './contexts/BalanceContext'
import { ToastProvider } from './contexts/ToastContext'
import Layout from './components/Layout'
import Login from './pages/Login'
import Register from './pages/Register'
import ForgotPassword from './pages/ForgotPassword'
import Home from './pages/Home'
import Dashboard from './pages/Dashboard'
import Analytics from './pages/Analytics'
import HowAppWorks from './pages/HowAppWorks'
import BudgetPlanning from './pages/BudgetPlanning'
import Categories from './pages/Categories'
import Transactions from './pages/Transactions'
import Profile from './pages/Profile'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import Onboarding from './pages/Onboarding'
import ErrorBoundary from './components/ErrorBoundary'
import AppSkeleton from './components/skeletons/AppSkeleton'

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return <AppSkeleton />
  }

  return user ? children : <Navigate to="/login" replace />
}

// Public Route Component (redirect if authenticated or if onboarding not completed)
function PublicRoute({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return <AppSkeleton />
  }

  // Check if user has completed onboarding
  const onboardingCompleted = localStorage.getItem('onboardingCompleted') === 'true'

  // If not logged in and onboarding not completed, redirect to onboarding
  if (!user && !onboardingCompleted) {
    return <Navigate to="/onboarding" replace />
  }

  return user ? <Navigate to="/home" replace /> : children
}

// Rate Wrapper for transition
function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Onboarding Route */}
        <Route path="/onboarding" element={<Onboarding />} />

        {/* Public Routes */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />

        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />

        <Route path="/register" element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } />

        <Route path="/forgot-password" element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        } />

        {/* Protected Routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/home" replace />} />
          <Route path="home" element={<Home />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="how-app-works" element={<HowAppWorks />} />
          <Route path="budget-planning" element={<BudgetPlanning />} />
          <Route path="categories" element={<Categories />} />
          <Route path="transactions" element={<Transactions />} />
          <Route path="profile" element={<Profile />} />
        </Route>

        {/* Catch all route - check onboarding first */}
        <Route path="*" element={
          localStorage.getItem('onboardingCompleted') === 'true'
            ? <Navigate to="/login" replace />
            : <Navigate to="/onboarding" replace />
        } />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <BalanceProvider>
              <Router>
                <AnimatedRoutes />
              </Router>
            </BalanceProvider>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App